import { CellType, useMappingStore } from "@/stores/mapping";
import { Skeleton } from "@/styles/ui/skeleton";
import { MemberType } from "@/types/mapping";
import { useMemo } from "react";
import { toast } from "sonner";
import { TagsAndRole, useMappingSearchResults } from "./hooks";
import { NoSearchResult } from "./no-search-result";
import { NonValueCell } from "./non-value-cell";
import { MappingSearchResultItem } from "./search-result-item";
import { MappingSearchResultList } from "./search-result-list";
import { MemberViewType } from "./types";
import { useTagClick } from "./use-tag-click";

function flattenNode(node: MemberViewType): MemberViewType[] {
  const flattenedNodes: MemberViewType[] = [node];
  node.children.forEach((child: MemberType & MemberViewType) => {
    flattenedNodes.push(...flattenNode(child));
  });

  return flattenedNodes.sort((a, b) => a.label.localeCompare(b.label, "ko"));
}

export const MappingSearchResultLineItemsSection = ({
  noResultActionComponent,
}: {
  noResultActionComponent: React.ReactNode;
}) => {
  const dimension = useMappingStore((state) => state.dimension.dimension);
  const {
    data: tags,
    isLoading,
    isError,
    refetch,
  } = useMappingSearchResults(dimension);
  const selectedFact = useMappingStore((state) => state.fact.selected);
  const handleTagClick = useTagClick({
    toast: { onError: () => toast.error("태그 추가에 실패했습니다.") },
  });

  const flattenedTags = useMemo(() => {
    const nodes: { [key: string]: MemberViewType[] } = {};
    Array.from(tags.values()).forEach(({ tags: tagList }) => {
      nodes[tagList[0].id] = tagList[0].children
        .map((child: MemberViewType) => flattenNode(child))
        .flat()
        .filter((node: MemberViewType) => node.type === "LINE_ITEM");
    });
    return nodes;
  }, [tags]);
  const searchedTags = useMemo(() => {
    const nodes: { [key: string]: MemberViewType[] } = {};
    Object.entries(flattenedTags).forEach(([key, tags]) => {
      nodes[key] = tags.filter((tag: MemberViewType) => !tag.isAddedTag);
    });
    return nodes;
  }, [flattenedTags]);
  const addedTags = useMemo(() => {
    const nodes: { [key: string]: MemberViewType[] } = {};
    Object.entries(flattenedTags).forEach(([key, tags]) => {
      nodes[key] = tags.filter((tag: MemberViewType) => !!tag.isAddedTag);
    });
    return nodes;
  }, [flattenedTags]);

  if (isLoading) {
    return (
      <div className="flex pb-[12px] flex-col items-start self-stretch">
        <div className="flex py-[12px] px-[16px] h-[48px] items-center gap-[10px] self-stretch border-t-[1px] border-neutral-200">
          <Skeleton className="h-[14px] w-[120px] rounded-md bg-neutral-100" />
        </div>
        <div className="flex py-0 px-[16px] flex-col items-start gap-[20px] self-stretch">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton
              key={i}
              className="h-[36px] w-full rounded-[8px] bg-neutral-100"
            />
          ))}
        </div>
      </div>
    );
  }

  const hasAnySearchResult = Array.from(tags.entries()).reduce(
    (acc, [_, group]) => acc || (group.role !== null && group.tags.length > 0),
    false,
  );

  const render = () => {
    if (selectedFact?.cellType) {
      if (CellType.NONE == selectedFact?.cellType) {
        return <NonValueCell text="값이 없습니다." />;
      } else if (CellType.HEADER == selectedFact?.cellType) {
        return <NonValueCell text="헤더 영역입니다." />;
      }
    }
    if (isError) {
      return (
        <NoSearchResult
          text="앗, 태그를 불러오지 못했습니다."
          refetch={async () => {
            await refetch();
          }}
        />
      );
    } else if (
      Object.keys(searchedTags).length + Object.keys(addedTags).length >
      0
    ) {
      return (
        <>
          <LineItemTagsSection
            allTags={tags}
            targetTags={addedTags}
            label="직접 추가한 라인아이템"
            onTagClick={handleTagClick}
          />
          <LineItemTagsSection
            allTags={tags}
            targetTags={searchedTags}
            label="추천 라인아이템"
            onTagClick={handleTagClick}
          />
          {noResultActionComponent}
        </>
      );
    } else if (!hasAnySearchResult || !selectedFact) {
      return (
        <div className="w-full h-full flex flex-col pb-4">
          <NoSearchResult
            text="검색 결과가 없습니다."
            refetch={async () => {
              await refetch();
            }}
            showRefreshButton={false}
          />
          {noResultActionComponent}
        </div>
      );
    }
  };

  return render();
};

const LineItemTagsSection = ({
  allTags,
  targetTags,
  label,
  onTagClick,
}: {
  allTags: Map<string, TagsAndRole>;
  targetTags: { [key: string]: MemberViewType[] };
  label: string;
  onTagClick: (tag: MemberViewType) => void;
}) => {
  return (
    <>
      {Array.from(allTags.entries()).map(
        ([roleKey, group]) =>
          group.tags[0]?.label &&
          targetTags[group.tags[0].id] &&
          targetTags[group.tags[0].id].length > 0 && (
            <MappingSearchResultList
              label={label}
              key={roleKey}
              className="pb-[12px]"
            >
              <div className="flex py-0 px-[16px] flex-col items-start gap-[20px] self-stretch">
                {targetTags[group.tags[0].id]
                  ?.sort((a, b) => a.label.localeCompare(b.label))
                  ?.map((tag) => (
                    <MappingSearchResultItem
                      key={tag.id}
                      label={tag.label}
                      tag={tag}
                      onClick={onTagClick}
                    />
                  ))}
              </div>
            </MappingSearchResultList>
          ),
      )}
    </>
  );
};
